/**
 * Rigging Editor Component
 *
 * Canvas-based editor for defining joints and bones on a character image.
 * Users click to place joints and drag between joints to create bones.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  CharacterRig,
  Joint,
  Bone,
  BoneSegment,
  RiggingEditorState,
} from '../../types/RiggingTypes';
import {
  RadioButtonChecked,
  Timeline,
  Delete,
  Undo,
} from '@mui/icons-material';
import { Slider } from '@mui/material';
import { useCharacterImageUrl } from '../../services/characterStorageService';

interface RiggingEditorProps {
  character: CharacterRig;
  onSave: (rig: CharacterRig) => void | Promise<void>;
  onCancel: () => void;
}

// Generate unique ID for joints/bones
const generateId = () =>
  `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Predefined bone names for common humanoid rigs.
const PREDEFINED_BONE_NAMES = [
  'head',
  'torso',
  'pelvis',
  'left_upper_arm',
  'left_forearm',
  'left_hand',
  'right_upper_arm',
  'right_forearm',
  'right_hand',
  'left_upper_leg',
  'left_lower_leg',
  'left_foot',
  'right_upper_leg',
  'right_lower_leg',
  'right_foot',
] as const;

function clampNormalizedValue(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampNormalizedBounds(bounds: BoneSegment['bounds']): BoneSegment['bounds'] {
  const x = clampNormalizedValue(bounds.x);
  const y = clampNormalizedValue(bounds.y);
  const maxX = clampNormalizedValue(bounds.x + bounds.width);
  const maxY = clampNormalizedValue(bounds.y + bounds.height);

  return {
    x,
    y,
    width: Math.max(0, maxX - x),
    height: Math.max(0, maxY - y),
  };
}

function createBoneSegment(
  bone: Bone,
  joints: Joint[]
): BoneSegment | null {
  const startJoint = joints.find((joint) => joint.id === bone.startJointId);
  const endJoint = joints.find((joint) => joint.id === bone.endJointId);

  if (!startJoint || !endJoint) {
    return null;
  }

  const dx = endJoint.position.x - startJoint.position.x;
  const dy = endJoint.position.y - startJoint.position.y;
  const boneAngle = Math.atan2(dy, dx);
  const boneLength = Math.sqrt(dx * dx + dy * dy);
  const midX = (startJoint.position.x + endJoint.position.x) / 2;
  const midY = (startJoint.position.y + endJoint.position.y) / 2;
  const halfLength = boneLength / 2 + 0.02;
  const halfWidth = (bone.width || 0.12) / 2;
  const cos = Math.abs(Math.cos(boneAngle));
  const sin = Math.abs(Math.sin(boneAngle));
  const boxW = halfLength * cos + halfWidth * sin;
  const boxH = halfLength * sin + halfWidth * cos;

  return {
    boneId: bone.id,
    boneName: bone.name,
    bounds: clampNormalizedBounds({
      x: midX - boxW,
      y: midY - boxH,
      width: boxW * 2,
      height: boxH * 2,
    }),
    pivot: startJoint.position,
    rotation: boneAngle,
  };
}

function createPelvisSegment(
  bones: Bone[],
  joints: Joint[],
  existingSegments: BoneSegment[]
): BoneSegment | null {
  if (
    bones.some((bone) => /pelvis|hips?/i.test(bone.name)) ||
    existingSegments.some((segment) => segment.boneName === 'pelvis')
  ) {
    return null;
  }

  const torsoBone = bones.find((bone) => /(torso|body|chest|spine)/i.test(bone.name));
  const leftUpperLegBone = bones.find(
    (bone) => /left/i.test(bone.name) && /(upper_leg|thigh|leg)/i.test(bone.name)
  );
  const rightUpperLegBone = bones.find(
    (bone) => /right/i.test(bone.name) && /(upper_leg|thigh|leg)/i.test(bone.name)
  );

  if (!torsoBone || !leftUpperLegBone || !rightUpperLegBone) {
    return null;
  }

  const torsoStartJoint = joints.find((joint) => joint.id === torsoBone.startJointId);
  const torsoEndJoint = joints.find((joint) => joint.id === torsoBone.endJointId);
  const leftHipJoint = joints.find((joint) => joint.id === leftUpperLegBone.startJointId);
  const rightHipJoint = joints.find(
    (joint) => joint.id === rightUpperLegBone.startJointId
  );
  const leftKneeJoint = joints.find((joint) => joint.id === leftUpperLegBone.endJointId);
  const rightKneeJoint = joints.find(
    (joint) => joint.id === rightUpperLegBone.endJointId
  );

  if (
    !torsoStartJoint ||
    !torsoEndJoint ||
    !leftHipJoint ||
    !rightHipJoint ||
    !leftKneeJoint ||
    !rightKneeJoint
  ) {
    return null;
  }

  const lowerTorsoJoint =
    torsoStartJoint.position.y > torsoEndJoint.position.y
      ? torsoStartJoint
      : torsoEndJoint;
  const averageHipX = (leftHipJoint.position.x + rightHipJoint.position.x) / 2;
  const averageHipY = (leftHipJoint.position.y + rightHipJoint.position.y) / 2;
  const hipSpread = Math.abs(leftHipJoint.position.x - rightHipJoint.position.x);
  const upperLegReach = Math.max(
    Math.abs(leftKneeJoint.position.y - leftHipJoint.position.y),
    Math.abs(rightKneeJoint.position.y - rightHipJoint.position.y)
  );

  const bounds = clampNormalizedBounds({
    x:
      Math.min(leftHipJoint.position.x, rightHipJoint.position.x) -
      Math.max(0.035, hipSpread * 0.2),
    y:
      Math.min(lowerTorsoJoint.position.y, averageHipY) -
      Math.max(0.04, upperLegReach * 0.15),
    width:
      hipSpread + Math.max(0.07, hipSpread * 0.4),
    height:
      Math.max(leftHipJoint.position.y, rightHipJoint.position.y) -
      Math.min(lowerTorsoJoint.position.y, averageHipY) +
      Math.max(0.08, upperLegReach * 0.22),
  });

  if (bounds.width < 0.04 || bounds.height < 0.04) {
    return null;
  }

  return {
    boneId: torsoBone.id,
    boneName: 'pelvis',
    animationBoneName: torsoBone.name,
    bounds,
    pivot: {
      x: averageHipX,
      y: Math.max(lowerTorsoJoint.position.y, averageHipY - upperLegReach * 0.08),
    },
    rotation: 0,
  };
}

const RiggingEditor: React.FC<RiggingEditorProps> = ({
  character,
  onSave,
  onCancel,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  // The stored imageUrl may be a relative blob path in the user's storage
  // directory; resolve it with the user SAS token for display.
  const imageSrc = useCharacterImageUrl(character.imageUrl);

  // Local state for editing
  const [joints, setJoints] = useState<Joint[]>(character.joints || []);
  const [bones, setBones] = useState<Bone[]>(character.bones || []);
  const [segments, setSegments] = useState<BoneSegment[]>(
    character.segments || []
  );
  const [characterName, setCharacterName] = useState(
    character.name || character.identity?.name || character.imageName
  );
  const [characterDescription, setCharacterDescription] = useState(
    character.description || character.identity?.description || ''
  );
  const [characterGender, setCharacterGender] = useState(
    character.identity?.gender || ''
  );
  const [characterSpecies, setCharacterSpecies] = useState(
    character.identity?.species || ''
  );
  const [characterPersonality, setCharacterPersonality] = useState(
    (character.identity?.personality ?? []).join(', ')
  );
  const [editorState, setEditorState] = useState<RiggingEditorState>({
    selectedJointId: null,
    selectedBoneId: null,
    isPlacingJoint: true, // Start in joint placement mode
    isConnectingBone: false,
    connectingFromJointId: null,
    zoom: 1,
    panOffset: { x: 0, y: 0 },
  });

  // TODO: Add selectedSegmentId state when implementing editable segments

  // Joint name counter for auto-naming
  const [jointCounter, setJointCounter] = useState(joints.length + 1);

  useEffect(() => {
    setJoints(character.joints || []);
    setBones(character.bones || []);
    setSegments(character.segments || []);
    setCharacterName(character.name || character.identity?.name || character.imageName);
    setCharacterDescription(
      character.description || character.identity?.description || ''
    );
    setCharacterGender(character.identity?.gender || '');
    setCharacterSpecies(character.identity?.species || '');
    setCharacterPersonality((character.identity?.personality ?? []).join(', '));
    setEditorState({
      selectedJointId: null,
      selectedBoneId: null,
      isPlacingJoint: true,
      isConnectingBone: false,
      connectingFromJointId: null,
      zoom: 1,
      panOffset: { x: 0, y: 0 },
    });
    setJointCounter((character.joints || []).length + 1);
  }, [character]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imageRef.current;

    if (!canvas || !ctx || !img) return;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate image positioning to fit in canvas while maintaining aspect ratio
    const scale = Math.min(
      (canvas.width * 0.8) / img.width,
      (canvas.height * 0.8) / img.height
    );
    const imgWidth = img.width * scale;
    const imgHeight = img.height * scale;
    const imgX = (canvas.width - imgWidth) / 2;
    const imgY = (canvas.height - imgHeight) / 2;

    // Draw image
    ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);

    // Helper to convert normalized position to canvas position
    const toCanvasPos = (pos: { x: number; y: number }) => ({
      x: imgX + pos.x * imgWidth,
      y: imgY + pos.y * imgHeight,
    });

    // Draw bones
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';

    bones.forEach((bone) => {
      const startJoint = joints.find((j) => j.id === bone.startJointId);
      const endJoint = joints.find((j) => j.id === bone.endJointId);

      if (startJoint && endJoint) {
        const start = toCanvasPos(startJoint.position);
        const end = toCanvasPos(endJoint.position);

        // Draw bone line
        ctx.strokeStyle =
          editorState.selectedBoneId === bone.id
            ? '#36cedc'
            : 'rgba(255, 255, 255, 0.7)';
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      }
    });

    // Draw segment bounding boxes
    const segmentColors = [
      'rgba(255, 100, 100, 0.3)',
      'rgba(100, 255, 100, 0.3)',
      'rgba(100, 100, 255, 0.3)',
      'rgba(255, 255, 100, 0.3)',
      'rgba(255, 100, 255, 0.3)',
      'rgba(100, 255, 255, 0.3)',
    ];

    segments.forEach((segment, index) => {
      const color = segmentColors[index % segmentColors.length];
      const borderColor = color.replace('0.3', '0.8');
      const rectX = imgX + segment.bounds.x * imgWidth;
      const rectY = imgY + segment.bounds.y * imgHeight;
      const rectWidth = segment.bounds.width * imgWidth;
      const rectHeight = segment.bounds.height * imgHeight;
      const bone = bones.find((b) => b.id === segment.boneId);
      const isSyntheticSegment =
        Boolean(segment.animationBoneName) &&
        segment.animationBoneName !== segment.boneName;

      if (bone && !isSyntheticSegment) {
        const startJoint = joints.find((j) => j.id === bone.startJointId);
        const endJoint = joints.find((j) => j.id === bone.endJointId);

        if (startJoint && endJoint) {
          const start = toCanvasPos(startJoint.position);
          const end = toCanvasPos(endJoint.position);
          const cx = (start.x + end.x) / 2;
          const cy = (start.y + end.y) / 2;
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          const normToCanvasScale = imgWidth;
          const visualLength = length + 0.04 * normToCanvasScale;
          const visualWidth = (bone.width || 0.12) * normToCanvasScale;

          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(angle);
          ctx.fillStyle = color;
          ctx.fillRect(
            -visualLength / 2,
            -visualWidth / 2,
            visualLength,
            visualWidth
          );
          ctx.strokeStyle = borderColor;
          ctx.lineWidth = 2;
          ctx.strokeRect(
            -visualLength / 2,
            -visualWidth / 2,
            visualLength,
            visualWidth
          );
          ctx.restore();
        }
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 2;
        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      }

      // Draw segment label
      ctx.fillStyle = 'white';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        segment.boneName,
        rectX + rectWidth / 2,
        rectY + rectHeight / 2
      );

      // Draw pivot point indicator
      const pivot = toCanvasPos(segment.pivot);
      ctx.beginPath();
      ctx.arc(pivot.x, pivot.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = borderColor.replace('0.8', '1');
      ctx.fill();
    });

    // Draw connecting line if in bone connection mode
    if (editorState.isConnectingBone && editorState.connectingFromJointId) {
      const fromJoint = joints.find(
        (j) => j.id === editorState.connectingFromJointId
      );
      if (fromJoint) {
        const from = toCanvasPos(fromJoint.position);
        ctx.strokeStyle = 'rgba(54, 206, 220, 0.5)';
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        // Line to cursor position would go here - we'll update on mouse move
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // Draw joints
    joints.forEach((joint) => {
      const pos = toCanvasPos(joint.position);

      // Outer circle
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 10, 0, Math.PI * 2);
      ctx.fillStyle =
        editorState.selectedJointId === joint.id ? '#36cedc' : '#f39c12';
      ctx.fill();

      // Inner circle
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // Joint name label
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(joint.name, pos.x, pos.y - 15);
    });
  }, [joints, bones, segments, editorState, imageSrc]);

  // Load and draw image
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;

      // Set canvas size to match container
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
      }

      drawCanvas();
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Redraw when joints or bones change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Handle canvas click
  const handleCanvasClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const img = imageRef.current;
      if (!canvas || !img) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Calculate image bounds
      const scale = Math.min(
        (canvas.width * 0.8) / img.width,
        (canvas.height * 0.8) / img.height
      );
      const imgWidth = img.width * scale;
      const imgHeight = img.height * scale;
      const imgX = (canvas.width - imgWidth) / 2;
      const imgY = (canvas.height - imgHeight) / 2;

      // Convert to normalized position relative to image
      const normalizedX = (x - imgX) / imgWidth;
      const normalizedY = (y - imgY) / imgHeight;

      // Check if click is within image bounds
      if (
        normalizedX < 0 ||
        normalizedX > 1 ||
        normalizedY < 0 ||
        normalizedY > 1
      ) {
        // Click outside image - deselect
        setEditorState((prev) => ({
          ...prev,
          selectedJointId: null,
          selectedBoneId: null,
          isConnectingBone: false,
          connectingFromJointId: null,
        }));
        return;
      }

      // Check if clicking on existing joint
      const clickedJoint = joints.find((joint) => {
        const dist = Math.sqrt(
          Math.pow((joint.position.x - normalizedX) * imgWidth, 2) +
            Math.pow((joint.position.y - normalizedY) * imgHeight, 2)
        );
        return dist < 15; // 15px click radius
      });

      if (clickedJoint) {
        if (editorState.isConnectingBone && editorState.connectingFromJointId) {
          // Complete bone connection
          if (clickedJoint.id !== editorState.connectingFromJointId) {
            const newBone: Bone = {
              id: generateId(),
              name: `bone_${bones.length + 1}`,
              startJointId: editorState.connectingFromJointId,
              endJointId: clickedJoint.id,
            };
            setBones((prev) => [...prev, newBone]);
          }
          setEditorState((prev) => ({
            ...prev,
            isConnectingBone: false,
            connectingFromJointId: null,
            selectedJointId: clickedJoint.id,
          }));
        } else if (!editorState.isPlacingJoint) {
          // Start bone connection
          setEditorState((prev) => ({
            ...prev,
            selectedJointId: clickedJoint.id,
            isConnectingBone: true,
            connectingFromJointId: clickedJoint.id,
          }));
        } else {
          // Just select the joint
          setEditorState((prev) => ({
            ...prev,
            selectedJointId: clickedJoint.id,
            selectedBoneId: null,
          }));
        }
      } else if (editorState.isPlacingJoint) {
        // Place new joint
        const newJoint: Joint = {
          id: generateId(),
          name: `joint_${jointCounter}`,
          position: { x: normalizedX, y: normalizedY },
          parentId: null,
        };
        setJoints((prev) => [...prev, newJoint]);
        setJointCounter((prev) => prev + 1);
        setEditorState((prev) => ({
          ...prev,
          selectedJointId: newJoint.id,
        }));
      } else {
        // Cancel bone connection and deselect
        setEditorState((prev) => ({
          ...prev,
          isConnectingBone: false,
          connectingFromJointId: null,
          selectedJointId: null,
        }));
      }
    },
    [joints, bones, editorState, jointCounter]
  );

  // Handle mode toggle
  const toggleMode = (mode: 'joint' | 'bone') => {
    setEditorState((prev) => ({
      ...prev,
      isPlacingJoint: mode === 'joint',
      isConnectingBone: false,
      connectingFromJointId: null,
    }));
  };

  // Delete selected joint
  const deleteSelectedJoint = () => {
    if (editorState.selectedJointId) {
      // Remove joint
      setJoints((prev) =>
        prev.filter((j) => j.id !== editorState.selectedJointId)
      );
      // Remove any bones connected to this joint
      setBones((prev) =>
        prev.filter(
          (b) =>
            b.startJointId !== editorState.selectedJointId &&
            b.endJointId !== editorState.selectedJointId
        )
      );
      setEditorState((prev) => ({
        ...prev,
        selectedJointId: null,
      }));
    }
  };

  // Undo last action
  const handleUndo = () => {
    if (bones.length > 0) {
      setBones((prev) => prev.slice(0, -1));
    } else if (joints.length > 0) {
      setJoints((prev) => prev.slice(0, -1));
    }
  };

  // Rename a bone
  const renameBone = (boneId: string, newName: string) => {
    setBones((prev) =>
      prev.map((bone) =>
        bone.id === boneId ? { ...bone, name: newName } : bone
      )
    );
  };

  // Update bone width
  const updateBoneWidth = (boneId: string, width: number) => {
    setBones((prev) =>
      prev.map((bone) => (bone.id === boneId ? { ...bone, width } : bone))
    );
  };

  // Get available bone names (not already used)
  const getAvailableBoneNames = (currentBoneName: string) => {
    const usedNames = bones.map((b) => b.name);
    return PREDEFINED_BONE_NAMES.filter(
      (name) => name === currentBoneName || !usedNames.includes(name)
    );
  };

  // Generate bounding boxes for all bones
  const generateBoneSegments = useCallback((): BoneSegment[] => {
    const baseSegments = bones
      .map((bone) => createBoneSegment(bone, joints))
      .filter((segment): segment is BoneSegment => segment !== null);
    const pelvisSegment = createPelvisSegment(bones, joints, baseSegments);

    return pelvisSegment ? [...baseSegments, pelvisSegment] : baseSegments;
  }, [bones, joints]);

  // Auto-regenerate segments when bones change
  useEffect(() => {
    const newSegments = generateBoneSegments();
    setSegments(newSegments);
  }, [bones, joints, generateBoneSegments]);

  // Save the rig
  const handleSave = () => {
    const trimmedName = characterName.trim();
    const trimmedDescription = characterDescription.trim();
    const trimmedGender = characterGender.trim();
    const trimmedSpecies = characterSpecies.trim();
    const personalityTraits = characterPersonality
      .split(',')
      .map((trait) => trait.trim())
      .filter(Boolean);
    const updatedRig: CharacterRig = {
      ...character,
      name: trimmedName || character.name || character.identity?.name || character.imageName,
      description: trimmedDescription,
      identity: {
        ...(character.identity || {}),
        name:
          trimmedName || character.name || character.identity?.name || character.imageName,
        description: trimmedDescription,
        gender: trimmedGender || undefined,
        species: trimmedSpecies || undefined,
        personality: personalityTraits,
        tags: character.tags ?? character.identity?.tags ?? [],
      },
      joints,
      bones,
      segments, // Include auto-generated segments
      updatedAt: new Date().toISOString(),
    };
    onSave(updatedRig);
  };

  return (
    <div className="rigging-editor">
      {/* Canvas Container */}
      <div className="editor-canvas-container">
        <canvas
          ref={canvasRef}
          className="editor-canvas"
          onClick={handleCanvasClick}
        />
      </div>

      {/* Sidebar */}
      <div className="editor-sidebar">
        {/* Toolbar */}
        <div className="editor-toolbar">
          <button
            className={`toolbar-btn ${editorState.isPlacingJoint ? 'active' : ''}`}
            onClick={() => toggleMode('joint')}
            title="Place Joints"
          >
            <RadioButtonChecked />
            Joints
          </button>
          <button
            className={`toolbar-btn ${!editorState.isPlacingJoint ? 'active' : ''}`}
            onClick={() => toggleMode('bone')}
            title="Connect Bones"
          >
            <Timeline />
            Bones
          </button>
          <button className="toolbar-btn" onClick={handleUndo} title="Undo">
            <Undo />
            Undo
          </button>
        </div>

        <div className="editor-section">
          <h3>Character Identity</h3>
          <div className="identity-fields">
            <label className="identity-field">
              <span>Name</span>
              <input
                type="text"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                placeholder="Character name"
              />
            </label>
            <label className="identity-field">
              <span>Description</span>
              <textarea
                value={characterDescription}
                onChange={(e) => setCharacterDescription(e.target.value)}
                placeholder="Short character bio"
              />
            </label>
            <label className="identity-field">
              <span>Gender</span>
              <input
                type="text"
                value={characterGender}
                onChange={(e) => setCharacterGender(e.target.value)}
                placeholder="e.g. male, female, non-binary"
              />
            </label>
            <label className="identity-field">
              <span>Species</span>
              <input
                type="text"
                value={characterSpecies}
                onChange={(e) => setCharacterSpecies(e.target.value)}
                placeholder="e.g. human, cat, robot"
              />
            </label>
            <label className="identity-field">
              <span>Personality</span>
              <input
                type="text"
                value={characterPersonality}
                onChange={(e) => setCharacterPersonality(e.target.value)}
                placeholder="Comma-separated traits, e.g. curious, kind"
              />
            </label>
          </div>
        </div>

        {/* Instructions */}
        <div className="editor-section">
          <h3>Instructions</h3>
          <p
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.85rem',
              margin: 0,
            }}
          >
            {editorState.isPlacingJoint
              ? '• Click on the image to place joint points (pivots)'
              : '• Click a joint, then click another to connect them with a bone'}
          </p>
        </div>

        {/* Joints List */}
        <div className="editor-section">
          <h3>Joints ({joints.length})</h3>
          <div className="joint-list">
            {joints.map((joint) => (
              <div
                key={joint.id}
                className={`joint-item ${editorState.selectedJointId === joint.id ? 'selected' : ''}`}
                onClick={() =>
                  setEditorState((prev) => ({
                    ...prev,
                    selectedJointId: joint.id,
                  }))
                }
              >
                <span>{joint.name}</span>
                <button
                  className="item-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditorState((prev) => ({
                      ...prev,
                      selectedJointId: joint.id,
                    }));
                    deleteSelectedJoint();
                  }}
                >
                  <Delete style={{ fontSize: 16 }} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Bones List */}
        <div className="editor-section">
          <h3>Bones ({bones.length})</h3>
          <p
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.75rem',
              margin: '0 0 8px 0',
            }}
          >
            Name each bone to enable action animations
          </p>
          <div className="bone-list">
            {bones.map((bone) => {
              const startJoint = joints.find((j) => j.id === bone.startJointId);
              const endJoint = joints.find((j) => j.id === bone.endJointId);
              const availableNames = getAvailableBoneNames(bone.name);
              return (
                <div
                  key={bone.id}
                  className={`bone-item ${editorState.selectedBoneId === bone.id ? 'selected' : ''}`}
                  onClick={() =>
                    setEditorState((prev) => ({
                      ...prev,
                      selectedBoneId: bone.id,
                    }))
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'rgba(255,255,255,0.5)',
                      }}
                    >
                      {startJoint?.name} → {endJoint?.name}
                    </span>
                    <select
                      value={bone.name}
                      onChange={(e) => {
                        e.stopPropagation();
                        renameBone(bone.id, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: '#2a2a4a',
                        border: '1px solid #4a4a6a',
                        borderRadius: '4px',
                        color: '#fff',
                        padding: '4px 8px',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Always include current value even if not in predefined list */}
                      {!PREDEFINED_BONE_NAMES.includes(bone.name as any) && (
                        <option value={bone.name}>{bone.name}</option>
                      )}
                      {availableNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>

                    {/* Width Slider */}
                    {editorState.selectedBoneId === bone.id && (
                      <div
                        style={{ marginTop: '4px', padding: '0 4px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: 'rgba(255,255,255,0.5)',
                            marginBottom: '-4px',
                          }}
                        >
                          Width
                        </div>
                        <Slider
                          size="small"
                          value={bone.width || 0.12}
                          min={0.05}
                          max={0.5}
                          step={0.01}
                          onChange={(_, val) =>
                            updateBoneWidth(bone.id, val as number)
                          }
                          sx={{
                            color: '#36cedc',
                            '& .MuiSlider-thumb': {
                              width: 12,
                              height: 12,
                            },
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    className="item-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBones((prev) => prev.filter((b) => b.id !== bone.id));
                    }}
                  >
                    <Delete style={{ fontSize: 16 }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="editor-actions">
          <button className="editor-btn cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="editor-btn save" onClick={handleSave}>
            Save Rig
          </button>
        </div>
      </div>
    </div>
  );
};

export default RiggingEditor;
